/**
 * Auth Adapter
 *
 * Provides unified auth interface using Supabase.
 *
 * Usage:
 *   const auth = getAuthAdapter();
 *   const user = await auth.signInWithEmail(email, password);
 *
 * @module services/auth/authAdapter
 */
import type { IAuthAdapter, UnifiedAuthUser, AuthStateEvent } from '../../types/auth';

// Supabase imports
import * as supabaseAuth from './supabaseAuth';
import type { User as SupabaseUser } from '@supabase/supabase-js';

// =============================================================================
// User Conversion Helpers
// =============================================================================

/**
 * Convert Supabase user to unified user type.
 */
function supabaseUserToUnified(user: SupabaseUser): UnifiedAuthUser {
  return {
    uid: user.id,
    email: user.email ?? null,
    emailVerified: !!user.email_confirmed_at,
    displayName: user.user_metadata?.display_name ?? user.user_metadata?.full_name ?? null,
    photoURL: user.user_metadata?.avatar_url ?? null,
  };
}

/**
 * Map Supabase auth events to unified events.
 */
function mapSupabaseEvent(event: string): AuthStateEvent {
  const eventMap: Record<string, AuthStateEvent> = {
    INITIAL_SESSION: 'INITIAL_SESSION',
    SIGNED_IN: 'SIGNED_IN',
    SIGNED_OUT: 'SIGNED_OUT',
    TOKEN_REFRESHED: 'TOKEN_REFRESHED',
    USER_UPDATED: 'USER_UPDATED',
    PASSWORD_RECOVERY: 'PASSWORD_RECOVERY',
  };
  return eventMap[event] || 'USER_UPDATED';
}

// =============================================================================
// Supabase Auth Adapter
// =============================================================================

/**
 * Supabase Auth Adapter implementation.
 */
class SupabaseAuthAdapter implements IAuthAdapter {
  async signInWithEmail(email: string, password: string): Promise<UnifiedAuthUser> {
    const { user } = await supabaseAuth.signInWithEmail(email, password);
    return supabaseUserToUnified(user);
  }

  async signUpWithEmail(email: string, password: string): Promise<UnifiedAuthUser | null> {
    const { user } = await supabaseAuth.signUpWithEmail(email, password);
    return user ? supabaseUserToUnified(user) : null;
  }

  async signOut(): Promise<void> {
    await supabaseAuth.signOut();
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    await supabaseAuth.sendPasswordResetEmail(email);
  }

  async resendVerificationEmail(email: string): Promise<void> {
    await supabaseAuth.resendVerificationEmail(email);
  }

  async getCurrentUser(): Promise<UnifiedAuthUser | null> {
    const user = await supabaseAuth.getCurrentUser();
    return user ? supabaseUserToUnified(user) : null;
  }

  async isEmailVerified(): Promise<boolean> {
    const user = await supabaseAuth.getCurrentUser();
    return user?.email_confirmed_at !== null && user?.email_confirmed_at !== undefined;
  }

  onAuthStateChange(
    callback: (event: AuthStateEvent, user: UnifiedAuthUser | null) => void
  ): () => void {
    return supabaseAuth.onAuthStateChange((event, session) => {
      const mappedEvent = mapSupabaseEvent(event);
      const user = session?.user ? supabaseUserToUnified(session.user) : null;
      callback(mappedEvent, user);
    });
  }

  async getIdToken(): Promise<string | null> {
    const session = await supabaseAuth.getSession();
    return session?.access_token ?? null;
  }
}

// =============================================================================
// Singleton Instance & Factory
// =============================================================================

let supabaseAdapter: SupabaseAuthAdapter | null = null;

/**
 * Get the auth adapter.
 *
 * @returns Auth adapter implementing IAuthAdapter
 */
export function getAuthAdapter(): IAuthAdapter {
  if (!supabaseAdapter) {
    supabaseAdapter = new SupabaseAuthAdapter();
  }
  return supabaseAdapter;
}

// Export adapter class for testing
export { SupabaseAuthAdapter };
